import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Bot, Send, UserCog, PlugZap, PlugZap2, AlertCircle,
  CheckCircle2, MessageCircle, Loader, Search, Copy, Sparkles,
  RefreshCw, Power, UserCheck,
} from "lucide-react";

export default function WhatsAppAI() {
  const [status, setStatus] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const [showTester, setShowTester] = useState(false);
  const msgEndRef = useRef(null);

  const loadStatus = useCallback(() => {
    api.get("/whatsapp/status")
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ configured: false, bot_enabled: true }));
  }, []);

  const loadConvs = useCallback(() => {
    setLoadingConvs(true);
    api.get("/whatsapp/conversations")
      .then((r) => setConversations(r.data || []))
      .catch(() => toast.error("Erro ao carregar conversas"))
      .finally(() => setLoadingConvs(false));
  }, []);

  const loadMessages = useCallback((phone) => {
    setLoadingMsgs(true);
    api.get(`/whatsapp/conversations/${phone}/messages`)
      .then((r) => setMessages(r.data || []))
      .catch(() => toast.error("Erro ao carregar mensagens"))
      .finally(() => setLoadingMsgs(false));
  }, []);

  useEffect(() => {
    loadStatus();
    loadConvs();
    const t = setInterval(() => {
      loadStatus();
      loadConvs();
      if (selected?.phone) loadMessages(selected.phone);
    }, 15000);
    return () => clearInterval(t);
  }, [loadStatus, loadConvs, loadMessages, selected]);

  useEffect(() => {
    if (selected?.phone) loadMessages(selected.phone);
  }, [selected, loadMessages]);

  useEffect(() => {
    msgEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter(
      (c) =>
        (c.sender_name || "").toLowerCase().includes(q) ||
        (c.phone || "").includes(q) ||
        (c.last_message || "").toLowerCase().includes(q)
    );
  }, [conversations, search]);

  const toggleBot = async () => {
    const next = !status?.bot_enabled;
    try {
      await api.patch("/whatsapp/toggle-bot", { enabled: next });
      setStatus((s) => ({ ...s, bot_enabled: next }));
      toast.success(next ? "IA ligada" : "IA pausada — só respondem humanos agora");
    } catch {
      toast.error("Erro ao alterar");
    }
  };

  const toggleTakeover = async () => {
    if (!selected) return;
    const next = !selected.human_takeover;
    try {
      await api.patch(`/whatsapp/conversations/${selected.phone}/takeover`, { enabled: next });
      setSelected((s) => ({ ...s, human_takeover: next }));
      setConversations((cs) =>
        cs.map((c) => (c.phone === selected.phone ? { ...c, human_takeover: next } : c))
      );
      toast.success(next ? "Humano assumiu — IA parou nessa conversa" : "IA voltou a responder");
    } catch {
      toast.error("Erro");
    }
  };

  const sendMessage = async () => {
    if (!draft.trim() || !selected) return;
    setSending(true);
    try {
      await api.post("/whatsapp/send", { phone: selected.phone, message: draft });
      setDraft("");
      loadMessages(selected.phone);
      loadConvs();
      toast.success("Enviado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao enviar");
    } finally {
      setSending(false);
    }
  };

  const configured = status?.configured;
  const connOk = status?.connection_ok;

  return (
    <div className="space-y-4" data-testid="whatsapp-ai-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-indigo-600 font-semibold mb-1 flex items-center gap-2">
            <Bot size={14} /> IA no WhatsApp
          </div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2">
            Atendente Virtual <Sparkles size={22} className="text-indigo-500" />
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">
            Gemini responde seus clientes 24h — você assume quando precisar.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowTester((v) => !v)}
            data-testid="wa-test-ai-btn"
            className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm border border-indigo-300 text-indigo-700 hover:bg-indigo-50 font-semibold"
          >
            <Sparkles size={13} /> Testar IA
          </button>
          <button
            onClick={toggleBot}
            data-testid="wa-toggle-bot-btn"
            className={`flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm font-semibold ${
              status?.bot_enabled
                ? "bg-emerald-600 hover:bg-emerald-500 text-white"
                : "bg-slate-200 hover:bg-slate-300 text-slate-700"
            }`}
          >
            <Power size={13} />
            {status?.bot_enabled ? "IA Ligada" : "IA Pausada"}
          </button>
          <button
            onClick={() => { loadStatus(); loadConvs(); if (selected) loadMessages(selected.phone); }}
            className="p-2 rounded-sm border border-slate-300 text-slate-600 hover:bg-slate-50"
            title="Atualizar"
            data-testid="wa-refresh-btn"
          >
            <RefreshCw size={13} />
          </button>
        </div>
      </header>

      {/* Status banner */}
      <StatusBanner status={status} />

      {showTester && <AiTester onClose={() => setShowTester(false)} />}

      {/* Chat layout */}
      <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-3 min-h-[560px]">
        {/* Lista de conversas */}
        <aside className="bg-white border border-slate-200 rounded-sm flex flex-col max-h-[640px]">
          <div className="p-3 border-b border-slate-100">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar..."
                data-testid="wa-search-input"
                className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-sm text-xs text-slate-900 placeholder:text-slate-400 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loadingConvs && (
              <div className="p-6 text-center text-slate-400 text-xs">Carregando...</div>
            )}
            {!loadingConvs && filtered.length === 0 && (
              <div className="p-6 text-center text-slate-400 text-xs">
                {configured
                  ? "Nenhuma conversa ainda. Mande uma msg pro número conectado pra testar."
                  : "Configure o Z-API no banner acima pra começar."}
              </div>
            )}
            {filtered.map((c) => (
              <button
                key={c.phone}
                onClick={() => setSelected(c)}
                data-testid={`wa-conv-${c.phone}`}
                className={`w-full text-left p-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                  selected?.phone === c.phone ? "bg-indigo-50 border-l-2 border-l-indigo-500" : ""
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-sm text-slate-800 truncate">
                    {c.sender_name || c.phone}
                  </div>
                  {c.unread_count > 0 && (
                    <span className="bg-indigo-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                      {c.unread_count}
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 truncate mt-0.5">{c.last_message}</div>
                <div className="flex items-center gap-2 mt-1">
                  <div className="text-[10px] text-slate-400 font-mono">{c.phone}</div>
                  {c.human_takeover && (
                    <span className="text-[9px] bg-amber-100 text-amber-700 border border-amber-300 px-1.5 py-0.5 rounded-full font-semibold flex items-center gap-0.5">
                      <UserCheck size={9} /> HUMANO
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </aside>

        {/* Chat */}
        <section className="bg-white border border-slate-200 rounded-sm flex flex-col max-h-[640px]">
          {!selected ? (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400 p-10">
              <MessageCircle size={48} className="mb-3 opacity-30" />
              <p className="text-sm">Selecione uma conversa</p>
            </div>
          ) : (
            <>
              <div className="p-3 border-b border-slate-100 flex items-center justify-between gap-2">
                <div>
                  <div className="font-semibold text-sm text-slate-800">
                    {selected.sender_name || selected.phone}
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono flex items-center gap-2">
                    {selected.phone}
                    <a
                      href={`https://wa.me/${selected.phone.replace(/\D/g, "")}`}
                      target="_blank" rel="noreferrer"
                      className="text-emerald-600 hover:text-emerald-500"
                      title="Abrir conversa no WhatsApp Web"
                    >
                      ↗
                    </a>
                  </div>
                </div>
                <button
                  onClick={toggleTakeover}
                  data-testid="wa-takeover-btn"
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm font-semibold ${
                    selected.human_takeover
                      ? "bg-amber-500 text-white hover:bg-amber-400"
                      : "bg-slate-100 text-slate-700 border border-slate-300 hover:bg-slate-200"
                  }`}
                >
                  <UserCog size={12} />
                  {selected.human_takeover ? "Humano no comando" : "Assumir conversa"}
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50">
                {loadingMsgs && (
                  <div className="text-center text-slate-400 text-xs">Carregando mensagens...</div>
                )}
                {!loadingMsgs && messages.length === 0 && (
                  <div className="text-center text-slate-400 text-xs">(sem mensagens)</div>
                )}
                {messages.map((m) => (
                  <MsgBubble key={m.id || m.created_at + m.role} m={m} />
                ))}
                <div ref={msgEndRef} />
              </div>

              <div className="p-3 border-t border-slate-100">
                <div className="flex gap-2 items-end">
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    placeholder={selected.human_takeover ? "Mensagem (humano)..." : "Mensagem manual (vai como humano)..."}
                    rows={2}
                    data-testid="wa-send-input"
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-sm text-sm text-slate-900 placeholder:text-slate-400 bg-white resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={sending || !draft.trim()}
                    data-testid="wa-send-btn"
                    className="bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-1.5"
                  >
                    {sending ? <Loader size={14} className="animate-spin" /> : <Send size={14} />}
                    Enviar
                  </button>
                </div>
                <p className="text-[10px] text-slate-400 mt-1.5">
                  Pressione Enter para enviar · Shift+Enter para nova linha
                </p>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

function MsgBubble({ m }) {
  const isUser = m.role === "user";
  const isBot = m.role === "assistant";
  const isAgent = m.role === "agent";
  let bubbleClass = "bg-white border border-slate-200 text-slate-800";
  let align = "justify-start";
  let label = "Cliente";
  if (isBot) {
    bubbleClass = "bg-indigo-500 text-white";
    align = "justify-end";
    label = "IA";
  } else if (isAgent) {
    bubbleClass = "bg-emerald-500 text-white";
    align = "justify-end";
    label = m.sent_by || "Atendente";
  } else if (isUser) {
    label = m.sender_name || "Cliente";
  }
  return (
    <div className={`flex ${align}`}>
      <div className={`max-w-[75%] px-3 py-2 rounded-xl ${bubbleClass} shadow-sm`}>
        <div className="text-[10px] opacity-70 font-semibold uppercase tracking-wider mb-0.5">{label}</div>
        <div className="text-sm whitespace-pre-wrap break-words">{m.content}</div>
        <div className="text-[9px] opacity-60 mt-1">
          {new Date(m.created_at).toLocaleString("pt-BR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" })}
        </div>
      </div>
    </div>
  );
}

function StatusBanner({ status }) {
  if (!status) return null;
  if (!status.configured) {
    return (
      <div className="bg-amber-50 border border-amber-300 rounded-sm p-4 flex items-start gap-3" data-testid="wa-status-banner">
        <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={18} />
        <div className="text-sm text-amber-900 flex-1">
          <div className="font-semibold mb-1">Z-API não configurado</div>
          <p className="text-xs leading-relaxed mb-2">
            Pra ativar o bot, acesse <a href="https://z-api.io" target="_blank" rel="noreferrer" className="underline font-semibold">z-api.io</a>,
            crie uma instância, conecte seu WhatsApp pelo QR Code e peça pro admin do sistema configurar as 4 variáveis no servidor:
          </p>
          <code className="text-[11px] bg-amber-100 border border-amber-200 rounded px-2 py-1 block font-mono">
            ZAPI_INSTANCE_ID<br/>ZAPI_INSTANCE_TOKEN<br/>ZAPI_CLIENT_TOKEN<br/>ZAPI_WEBHOOK_SECRET
          </code>
          <p className="text-[11px] mt-2">
            Depois, cole esta URL no painel Z-API (Webhooks → "Ao receber mensagem"):
          </p>
          <WebhookUrlHint />
        </div>
      </div>
    );
  }
  if (status.connection_ok === false) {
    return (
      <div className="bg-red-50 border border-red-300 rounded-sm p-4 flex items-start gap-3" data-testid="wa-status-banner">
        <PlugZap className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
        <div className="text-sm text-red-900">
          <div className="font-semibold">Z-API desconectado</div>
          <p className="text-xs">Reabra o painel Z-API e escaneie o QR Code novamente.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="bg-emerald-50 border border-emerald-300 rounded-sm p-3 flex items-center gap-3" data-testid="wa-status-banner">
      <CheckCircle2 className="text-emerald-600" size={16} />
      <div className="text-sm text-emerald-900">
        <strong>Conectado</strong>
        {status.phone_connected && <span className="text-xs ml-2 font-mono">· {status.phone_connected}</span>}
        <span className="text-xs ml-3 opacity-70">
          IA: {status.bot_enabled ? "ligada 🤖" : "pausada 💤"}
        </span>
      </div>
    </div>
  );
}

function WebhookUrlHint() {
  const url = `${window.location.origin.replace("preview.emergentagent.com", "preview.emergentagent.com")}/api/whatsapp/webhook/COLE_SEU_ZAPI_WEBHOOK_SECRET_AQUI`;
  const copy = () => {
    navigator.clipboard.writeText(url);
    toast.success("URL copiada");
  };
  return (
    <div className="flex items-center gap-2 mt-1 bg-white border border-amber-200 rounded px-2 py-1">
      <code className="text-[10px] font-mono text-slate-700 flex-1 break-all">{url}</code>
      <button onClick={copy} className="text-amber-700 hover:text-amber-900" title="Copiar">
        <Copy size={12} />
      </button>
    </div>
  );
}

function AiTester({ onClose }) {
  const [msg, setMsg] = useState("");
  const [phone, setPhone] = useState("5549999998888");
  const [history, setHistory] = useState([]);
  const [execute, setExecute] = useState(false);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!msg.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/whatsapp/test-ai-reply", { phone, message: msg, history, execute });
      setHistory([...history, { role: "user", content: msg }, { role: "assistant", content: data.reply, action: data.action }]);
      setMsg("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao testar IA");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-indigo-50 border border-indigo-300 rounded-sm p-4 space-y-3" data-testid="wa-ai-tester">
      <div className="flex items-center justify-between">
        <div className="font-semibold text-sm text-indigo-900 flex items-center gap-1.5">
          <Sparkles size={14} /> Testar conversa com a IA (não envia WhatsApp ao cliente)
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setHistory([])} data-testid="wa-test-reset" className="text-indigo-600 hover:text-indigo-800 text-xs">↺ nova conversa</button>
          <button onClick={onClose} className="text-indigo-600 hover:text-indigo-800 text-xs">✕ fechar</button>
        </div>
      </div>
      {history.length > 0 && (
        <div className="bg-white border border-indigo-200 rounded-sm p-3 space-y-2 max-h-80 overflow-y-auto" data-testid="wa-test-history">
          {history.map((m, i) => (
            <div key={i} className={`text-sm whitespace-pre-wrap ${m.role === "user" ? "text-right" : ""}`}>
              <span className={`inline-block px-3 py-1.5 rounded-sm ${m.role === "user" ? "bg-slate-100 text-slate-800" : "bg-indigo-50 text-slate-800"}`}>{m.content}</span>
              {m.action && (
                <div className="text-[10px] font-mono text-indigo-700 mt-1" data-testid={`wa-test-action-${i}`}>
                  ação: {m.action.tipo} {execute ? "(executada)" : "(simulada)"}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr_auto] gap-2">
        <input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Telefone do cliente"
          data-testid="wa-test-phone"
          className="px-3 py-2 border border-slate-300 rounded-sm text-sm text-slate-900 placeholder:text-slate-400 bg-white"
        />
        <input
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && run()}
          placeholder="Ex: Oi, quanto fica trocar a tela do iPhone 11?"
          data-testid="wa-test-msg"
          className="px-3 py-2 border border-slate-300 rounded-sm text-sm text-slate-900 placeholder:text-slate-400 bg-white"
        />
        <button
          onClick={run}
          disabled={loading || !msg.trim()}
          data-testid="wa-test-run"
          className="bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-1.5"
        >
          {loading ? <Loader size={13} className="animate-spin" /> : <PlugZap2 size={13} />}
          Enviar
        </button>
      </div>
      <label className="flex items-center gap-2 text-xs text-indigo-900">
        <input type="checkbox" checked={execute} onChange={(e) => setExecute(e.target.checked)} data-testid="wa-test-execute" />
        Executar ações de verdade (cria a OS no sistema e avisa seu WhatsApp)
      </label>
    </div>
  );
}
